import { odooApi } from '../index';
import { PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { extractErrorMessage } from '../transforms';

export interface ElectronicForm {
  id: string;
  name: string;
  description: string;
  url: string;
  smartFormName?: string | null;
  createdAt?: string;
}

interface OdooElectronicForm {
  id?: number;
  name?: string;
  description?: string;
  url?: string;
  smart_form_name?: string | null;
  create_date?: string;
}

interface OdooElectronicFormsResponse {
  success: boolean;
  data: OdooElectronicForm[];
  pagination?: {
    total_records?: number;
    total_pages?: number;
    current_page?: number;
    page_size?: number;
  };
  language?: string;
}

export const electronicFormsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    getElectronicForms: builder.query<
      PaginatedResponse<ElectronicForm>,
      { page?: number; pageSize?: number; lang?: 'ar' | 'en' | 'ku' }
    >({
      query: ({ page = 1, pageSize = 9, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);

        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
          lang: odooLang,
        });

        return {
          url: `/api/moys/electronic_forms?${params.toString()}`,
          method: 'POST',
        };
      },
      transformResponse: (response: OdooElectronicFormsResponse, _meta, arg) => {
        const pagination = response.pagination || {};
        const total = pagination.total_records ?? response.data?.length ?? 0;
        const currentPage = pagination.current_page ?? arg.page ?? 1;
        const pageSize = pagination.page_size ?? arg.pageSize ?? 9;
        const totalPages =
          pagination.total_pages ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 1);

        const forms: ElectronicForm[] = (response.data || []).map((form, index) => ({
          id: String(form.id ?? (currentPage - 1) * pageSize + index + 1),
          name: form.name || '',
          description: form.description || '',
          url: form.url || '',
          smartFormName: form.smart_form_name ?? null,
          createdAt: form.create_date || undefined,
        }));

        return {
          data: forms,
          total,
          page: currentPage,
          pageSize,
          totalPages,
        };
      },
      transformErrorResponse: (response) => extractErrorMessage(response),
      providesTags: (result) => {
        if (!result) {
          return [{ type: 'Form' as const, id: 'LIST' }];
        }

        return [
          ...result.data.map((form) => ({ type: 'Form' as const, id: form.id })),
          { type: 'Form' as const, id: 'LIST' },
        ];
      },
    }),
  }),
});

export const { useGetElectronicFormsQuery } = electronicFormsApi;


